import {
  NoContentContainer,
  Table,
  TableCell,
  TableContainer,
  TableRow,
} from "@/components/Table";
import { IconButton, Typography } from "@mui/material";
import CreateOutlinedIcon from "@mui/icons-material/CreateOutlined";
import { useQuery } from "@apollo/client";
import { useParams } from "react-router-dom";
import { ResultOf, graphql } from "@/config/graphql";

export const currentProductionDetails = graphql(`
  query currentProductionDetails($params: ReportParams!) {
    report(params: $params) {
      id
      humidity
      noProductionReason
      productionDetails {
        id
        resource {
          id
          name
        }
        sellQuantity
        destination {
          id
          code
          name
          type
        }
      }
      batchControlDetails {
        id
        initialHourometer
        finalHourometer
        waste
      }
    }
  }
`);

interface ProductionTableProps {
  onEdit?: (
    detail: NonNullable<
      ResultOf<typeof currentProductionDetails>["report"]["productionDetails"]
    >[number]
  ) => void;
  showPerformance?: boolean;
}
export const ProductionTable = (props: ProductionTableProps) => {
  const params = useParams();
  const { data } = useQuery(currentProductionDetails, {
    variables: { params: { id: +(params.id || 0) } },
    fetchPolicy: "cache-first",
    returnPartialData: true,
  });
  const totalInternalAmount =
    data?.report?.productionDetails?.reduce(
      (acc, detail) =>
        detail.destination.type === "internal"
          ? acc + (detail.sellQuantity ?? 0)
          : acc,
      0
    ) ?? 0;
  const totalExternalAmount =
    data?.report?.productionDetails?.reduce(
      (acc, detail) =>
        detail.destination.type === "external"
          ? acc + (detail.sellQuantity ?? 0)
          : acc,
      0
    ) ?? 0;
  const waste =
    data?.report?.batchControlDetails?.reduce(
      (acc, detail) => acc + (detail.waste ?? 0),
      0
    ) ?? 0;
  const hours =
    data?.report?.batchControlDetails?.reduce(
      (acc, detail) =>
        acc + (detail.finalHourometer ?? 0) - (detail.initialHourometer ?? 0),
      0
    ) ?? 0;
  return (
    <TableContainer>
      <Table>
        <thead>
          <TableRow variant="header">
            <TableCell></TableCell>
            <TableCell colSpan={2}>Producción</TableCell>
            <TableCell colSpan={2}>Cantidad (ton.)</TableCell>
          </TableRow>
          <TableRow variant="mainHeader">
            <TableCell sx={{ minWidth: 40 }}></TableCell>
            <TableCell>Tipo mezcla</TableCell>
            <TableCell>Destino</TableCell>
            <TableCell>Interno</TableCell>
            <TableCell>Externo</TableCell>
          </TableRow>
        </thead>
        <tbody>
          {data?.report?.productionDetails?.map((detail, index) => {
            return (
              <TableRow
                key={index}
                onClick={
                  props.onEdit ? () => props.onEdit?.(detail) : undefined
                }
              >
                <TableCell>
                  {props.onEdit && (
                    <IconButton
                      sx={{
                        color: "#333333",
                        padding: 0,
                        width: 24,
                        height: 24,
                      }}
                    >
                      <CreateOutlinedIcon />
                    </IconButton>
                  )}
                </TableCell>
                <TableCell>{detail.resource.name}</TableCell>
                <TableCell>{detail.destination.name}</TableCell>
                <TableCell textAlign="center">
                  {detail.destination.type === "internal"
                    ? detail.sellQuantity?.toLocaleString("es-CL")
                    : ""}
                </TableCell>
                <TableCell textAlign="center">
                  {detail.destination.type === "external"
                    ? detail.sellQuantity?.toLocaleString("es-CL")
                    : ""}
                </TableCell>
              </TableRow>
            );
          })}
          {Boolean(data?.report.productionDetails?.length) && (
            <>
              <TableRow>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell>
                  <Typography fontWeight={600} textAlign={"right"}>
                    Total por cliente
                  </Typography>
                </TableCell>
                <TableCell textAlign="center">
                  {totalInternalAmount.toLocaleString("es-CL")}
                </TableCell>
                <TableCell textAlign="center">
                  {totalExternalAmount.toLocaleString("es-CL")}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell>
                  <Typography fontWeight={600} textAlign={"right"}>
                    Total producción
                  </Typography>
                </TableCell>
                <TableCell textAlign="center" colSpan={2}>
                  {(totalInternalAmount + totalExternalAmount).toLocaleString(
                    "es-CL"
                  )}
                </TableCell>
              </TableRow>
            </>
          )}
          {Boolean(data?.report.productionDetails?.length) &&
            props.showPerformance && (
              <TableRow>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell>
                  <Typography fontWeight={600} textAlign={"right"}>
                    Rendimiento (ton/h)
                  </Typography>
                </TableCell>
                <TableCell textAlign="center" colSpan={2}>
                  {(
                    (totalInternalAmount + totalExternalAmount + waste) /
                    hours
                  ).toLocaleString("es-CL")}
                </TableCell>
              </TableRow>
            )}
        </tbody>
      </Table>
      {data?.report.productionDetails?.length === 0 && (
        <NoContentContainer>
          <Typography variant="body2">No hay mezclas declaradas</Typography>
        </NoContentContainer>
      )}
    </TableContainer>
  );
};
